import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircle, Sparkles, Ticket, QrCode, Share2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent } from "../ui/dialog";
import { EVENT } from "../../data/content";
import { SectionHeading } from "./SectionHeading";
import { ShareableCard } from "./ShareableCard";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { useLang, fill } from "../../i18n/LanguageContext";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-white/5 px-4 py-3.5 text-white placeholder-white/35 outline-none transition-all focus:border-gold-light/60 focus:ring-2 focus:ring-gold-primary/30";

const makeTicket = () => {
  const rnd = Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4).padEnd(4, "0");
  return `SC-${rnd}`;
};

const saveLocal = (reg) => {
  try {
    const list = JSON.parse(window.localStorage.getItem("sc_regs") || "[]");
    list.push(reg);
    window.localStorage.setItem("sc_regs", JSON.stringify(list));
    return list.length;
  } catch {
    return 1;
  }
};

const PaymentCard = ({ p, compact }) => (
  <div data-testid="payment-card" className={`rounded-2xl border border-gold-light/25 bg-ink-700/50 p-5 ${compact ? "" : "md:p-6"}`}>
    <div className="flex items-center gap-2 text-gold-light">
      <QrCode className="h-5 w-5" />
      <h4 className="font-display text-xl text-white">{p.title}</h4>
    </div>
    <p className="mt-2 text-sm leading-relaxed text-white/60">{p.subtitle}</p>
    <div className="mt-5 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
      <a href={EVENT.qrImage} target="_blank" rel="noopener noreferrer" className="shrink-0">
        <img
          src={EVENT.qrImage}
          alt="UPI payment QR code for Little Sapiens"
          data-testid="payment-qr"
          className="h-40 w-40 rounded-xl border border-white/15 bg-white object-contain p-1"
        />
        <span className="mt-1 block text-center text-[11px] text-white/45">{p.scanHint}</span>
      </a>
      <div className="w-full space-y-3 text-sm">
        <div className="flex items-baseline justify-between rounded-lg bg-gold-primary/15 px-3 py-2">
          <span className="text-white/70">{p.payeeLabel}</span>
          <span className="font-medium text-white">{EVENT.upiName}</span>
        </div>
        <div className="rounded-lg bg-white/5 px-3 py-2">
          <span className="block text-white/60">{p.upiLabel}</span>
          <span className="break-all font-medium text-gold-light">{EVENT.upiId}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-white/45">
          <span>BHIM · UPI</span>
          <span>·</span>
          <span>{EVENT.bank}</span>
        </div>
      </div>
    </div>
  </div>
);

export const Registration = ({ onRegistered }) => {
  const { t } = useLang();
  const s = t.registration;
  const [form, setForm] = useState({ child_name: "", age: "", category: "", parent_name: "", parent_phone: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [showShare, setShowShare] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const waConfirm = done
    ? `https://wa.me/91${EVENT.phone}?text=${encodeURIComponent(
        fill(s.waText, {
          name: done.child_name,
          age: done.age,
          cat: done.category,
          parent: done.parent_name,
          phone: done.parent_phone,
          city: done.city || "Patiala",
          ref: done.ticket_code,
        })
      )}`
    : `https://wa.me/91${EVENT.phone}`;

  const submit = (e) => {
    e.preventDefault();
    if (!form.child_name || !form.age || !form.category || !form.parent_name || !form.parent_phone) {
      toast.error(s.errFill);
      return;
    }
    const age = parseInt(form.age, 10);
    if (isNaN(age) || age < 3 || age > 15) {
      toast.error(s.errAge);
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const reg = { ...form, age, ticket_code: makeTicket(), created_at: new Date().toISOString() };
      const total = saveLocal(reg);
      onRegistered && onRegistered(total);
      setDone(reg);
      setLoading(false);
      toast.success(s.toast);
    }, 500);
  };

  return (
    <section id="register" data-testid="registration-section" className="relative overflow-hidden bg-ink-900 py-24 md:py-32">
      <div className="pointer-events-none absolute left-1/4 top-0 h-[420px] w-[420px] rounded-full bg-gold-primary/12 blur-[130px]" />
      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-6 md:px-8 lg:grid-cols-2">
        {/* Left */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div data-testid="urgency-badge" className="mb-5 inline-flex items-center gap-2 rounded-full bg-carpet/25 px-4 py-2 text-xs font-medium uppercase tracking-[0.15em] text-red-200">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-400" />
            </span>
            {s.urgency}
          </div>
          <SectionHeading eyebrow={s.eyebrow} title={s.title} accent={s.accent} align="left" testid="registration-heading" />

          <div className="mt-6 flex items-center gap-4 rounded-2xl border border-gold-light/25 bg-gold-primary/10 p-5">
            <Ticket className="h-9 w-9 text-gold-light" />
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-white/60">{s.feeLabel}</p>
              <p className="font-display text-4xl font-semibold text-gold-gradient">{EVENT.fee} <span className="text-lg text-white/60">{s.feeOnly}</span></p>
            </div>
          </div>

          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-white/65">{s.intro}</p>
          <ul className="mt-6 space-y-3">
            {s.perks.map((li) => (
              <li key={li} className="flex items-center gap-3 text-white/75">
                <Sparkles className="h-4 w-4 shrink-0 text-gold-light" /> {li}
              </li>
            ))}
          </ul>

          <div className="mt-8">
            <PaymentCard p={s.payment} />
          </div>
        </div>

        {/* Right */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce}>
          {done ? (
            <div data-testid="registration-success" className="glass rounded-3xl p-8 md:p-10">
              <div className="text-center">
                <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-gold-light to-gold-primary text-ink-900">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h3 className="font-display text-3xl text-white">{s.success.title}</h3>
                <p className="mt-3 text-white/70">
                  {s.success.ref} <span className="font-display text-xl text-gold-light">{done.ticket_code}</span>
                </p>
              </div>

              <div className="mt-6">
                <PaymentCard p={s.payment} compact />
              </div>

              <p className="mt-5 text-center text-sm text-white/55">{s.payment.after}</p>
              <a
                href={waConfirm}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-confirm-btn"
                className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 font-medium text-ink-900 transition-transform hover:scale-[1.02]"
              >
                <MessageCircle className="h-5 w-5" /> {s.payment.paidBtn}
              </a>
              <button
                onClick={() => setShowShare(true)}
                data-testid="open-share-card-btn"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-gold-light/40 px-8 py-3.5 text-sm font-medium text-gold-light transition-colors hover:bg-gold-light/10"
              >
                <Share2 className="h-4 w-4" /> {s.success.share}
              </button>
              <button onClick={() => setDone(null)} data-testid="register-another-btn" className="mt-4 block w-full text-sm text-white/50 underline-offset-4 hover:text-gold-light hover:underline">
                {s.success.another}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="registration-form" className="glass rounded-3xl p-7 md:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/60">{s.childName}</label>
                  <input data-testid="input-child-name" value={form.child_name} onChange={set("child_name")} placeholder={s.childNamePh} className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/60">{s.age}</label>
                  <input data-testid="input-age" type="number" min="3" max="15" value={form.age} onChange={set("age")} placeholder={s.agePh} className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/60">{s.city}</label>
                  <input data-testid="input-city" value={form.city} onChange={set("city")} placeholder={s.cityPh} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/60">{s.category}</label>
                  <Select value={form.category} onValueChange={set("category")}>
                    <SelectTrigger data-testid="select-category" className={`${inputCls} h-auto justify-between`}>
                      <SelectValue placeholder={s.categoryPh} />
                    </SelectTrigger>
                    <SelectContent className="border-white/15 bg-ink-700 text-white">
                      {s.categories.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`category-option-${c}`} className="focus:bg-gold-primary/20 focus:text-white">
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/60">{s.parentName}</label>
                  <input data-testid="input-parent-name" value={form.parent_name} onChange={set("parent_name")} placeholder={s.parentNamePh} className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/60">{s.phone}</label>
                  <input data-testid="input-parent-phone" value={form.parent_phone} onChange={set("parent_phone")} placeholder={s.phonePh} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/60">{s.note}</label>
                  <textarea data-testid="input-message" value={form.message} onChange={set("message")} rows={3} placeholder={s.notePh} className={`${inputCls} resize-none`} />
                </div>
              </div>

              <button type="submit" disabled={loading} data-testid="submit-registration-btn" className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold-light to-gold-primary py-4 font-medium text-ink-900 transition-shadow duration-300 hover:shadow-[0_0_28px_rgba(217,119,6,0.55)] disabled:opacity-60">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                {loading ? s.submitting : s.submit}
              </button>
              <p className="mt-4 text-center text-xs text-white/40">{s.disclaimer}</p>
            </form>
          )}
        </motion.div>
      </div>

      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent data-testid="share-modal" className="max-w-md border border-gold-light/20 bg-ink-800 text-white [&>button]:text-white/60 [&>button]:hover:text-gold-light">
          <h3 className="mb-3 font-display text-2xl text-white">{s.success.shareTitle}</h3>
          {done && <ShareableCard name={done.child_name} category={done.category} />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
